import { Fragment, useEffect, useMemo, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  X,
  Download,
  Columns3,
  Rows3,
  MoreHorizontal,
  Inbox,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { csvDe, descargarCsv, marcarGrupo, repartirEnGrupos, type Agrupacion } from './tabla-pura';
import { Superfiltro } from './Superfiltro';
import type { Faceta, ValoresDeFacetas } from '@/lib/superfiltro';

export type { Agrupacion };

/**
 * DATATABLE — la tabla de toda la tienda.
 *
 * Se construye UNA vez y la usan Ventas, Inventario, Productos, Gastos y el
 * libro de movimientos. Hoy cada pantalla apila tarjetas a mano: eso significa
 * cinco maneras de ordenar, cinco de filtrar y ninguna de exportar.
 *
 * ═══ LAS DECISIONES QUE NO SE VEN ═══
 *
 * **Los números van a la derecha y tabulares.** Con cifras proporcionales, una
 * columna de plata no se puede comparar de un vistazo: el 1 ocupa menos que el
 * 8 y las unidades no quedan alineadas. En un tablero de dinero eso importa.
 *
 * **Ordenar no pierde la selección.** Si marcaste ocho filas para confirmarlas
 * y ordenas por fecha, siguen marcadas: se guardan por id, no por posición.
 *
 * **Doble clic abre el detalle, un clic no.** Un clic simple sobre la fila es
 * lo que hace alguien para leer mejor; abrir un panel ahí es un panel que se
 * abre solo todo el tiempo. Las acciones concretas viven en el menú «⋯».
 *
 * **Exportar exporta lo que se ve.** Con los filtros aplicados y las columnas
 * visibles. Exportar todo cuando en pantalla hay doce filas es dar un archivo
 * que no se parece a lo que la persona estaba mirando.
 *
 * ═══ AGRUPADA (tanda 6, la tabla de «Clientes») ═══
 *
 * Con `agrupacion` la tabla es un tablero: un bloque por grupo (encabezado que se
 * pliega, sus filas con la franja de su color y su pie), todo dibujado de una vez,
 * sin páginas. Se desplaza dentro de su propia caja, y por eso puede dejar fijos
 * los encabezados de columna arriba y la primera columna a la izquierda: en el
 * teléfono se sabe de quién es la fila aunque se haya corrido hasta el final.
 * La casilla de marcar vive dentro de esa primera columna (y no en una propia)
 * para que haya una sola columna fija. Sin `agrupacion`, la tabla es la de siempre.
 */

export interface Columna<T> {
  /** Clave única. Es la que se guarda para columnas visibles y orden. */
  id: string;
  /** Encabezado. */
  titulo: string;
  /** Cómo se dibuja la celda. */
  celda: (fila: T) => ReactNode;
  /** Valor para ordenar y exportar. Sin esto, la columna no se puede ordenar. */
  valor?: (fila: T) => string | number | null | undefined;
  /** Números: derecha y tabular. */
  numerica?: boolean;
  /** Ancho sugerido (clase de Tailwind). */
  ancho?: string;
  /** Se puede esconder desde el selector de columnas. Por defecto sí. */
  fija?: boolean;
  /** Arranca oculta. */
  ocultaPorDefecto?: boolean;
  /** Pie de columna (totales). */
  total?: (filas: T[]) => ReactNode;
}

export interface AccionDeFila<T> {
  etiqueta: string;
  icono?: ReactNode;
  onClick: (fila: T) => void;
  /** Oculta la acción para esta fila. */
  ocultaSi?: (fila: T) => boolean;
  peligrosa?: boolean;
}

export interface AccionEnLote<T> {
  etiqueta: string;
  icono?: ReactNode;
  onClick?: (filas: T[]) => void;
  /** En lugar del botón, un control propio (un desplegable para elegir a dónde van las marcadas). */
  control?: (filas: T[]) => ReactNode;
}

export interface DataTableProps<T> {
  filas: T[];
  columnas: Columna<T>[];
  /** Id estable de cada fila. Sostiene la selección y el orden. */
  idDe: (fila: T) => string;

  cargando?: boolean;
  error?: string | null;
  onReintentar?: () => void;

  /** Texto del buscador. Si se omite, no hay buscador. */
  buscar?: string;
  onBuscar?: (v: string) => void;
  placeholderBuscar?: string;

  /** Chips de filtros activos, con su «quitar». */
  filtrosActivos?: Array<{ clave: string; etiqueta: string; onQuitar: () => void }>;
  onLimpiarFiltros?: () => void;

  /**
   * Las facetas del superfiltro (estado, categoría, fechas…) y sus valores, que la
   * pantalla guarda en la URL. `onFacetas` recibe los cambios de un gesto juntos.
   */
  facetas?: Faceta[];
  valoresDeFacetas?: ValoresDeFacetas;
  onFacetas?: (cambios: ValoresDeFacetas) => void;
  /** El contador ya dicho: «12 de 40 ventas». */
  resultados?: string;

  /** Controles propios del módulo (selects, rangos). Van en la barra. */
  barra?: ReactNode;

  onAbrirFila?: (fila: T) => void;
  accionesDeFila?: AccionDeFila<T>[];
  accionesEnLote?: AccionEnLote<T>[];

  /** Nombre del archivo al exportar. Sin esto no se muestra el botón. */
  exportarComo?: string;

  vacio?: { titulo: string; descripcion?: string; accion?: ReactNode };
  /** Filas por página. 0 = todas. Con `agrupacion` no hay páginas. */
  porPagina?: number;
  /** Clave para recordar columnas, densidad y grupos plegados entre sesiones. */
  memoria?: string;

  /** Filas en grupos, con encabezado, color y pie. Ver arriba. */
  agrupacion?: Agrupacion<T>;
  /**
   * Las columnas a la vista (ids), mandadas desde fuera: para que la elección viva donde el
   * módulo diga (el servidor) y no en este navegador. Sin esto, la tabla usa su `memoria`.
   */
  columnasVisibles?: string[];
  onColumnasVisibles?: (ids: string[]) => void;
  /** Lo mismo para la densidad. */
  densidad?: 'comoda' | 'compacta';
  onDensidad?: (densidad: 'comoda' | 'compacta') => void;
  /**
   * No dibuja su barra (buscador, `barra`, densidad, columnas y exportar): la pantalla trae la
   * suya y esos controles saldrían dos veces. Columnas y densidad se mandan entonces desde fuera.
   */
  sinBarraPropia?: boolean;
}

type Direccion = 'asc' | 'desc';

/** Lo que dentro de una fila ya hace algo con el clic: un doble clic ahí no es «abre el detalle». */
const INTERACTIVO = 'input, select, textarea, button, a, label';

export function DataTable<T>({
  filas,
  columnas,
  idDe,
  cargando,
  error,
  onReintentar,
  buscar,
  onBuscar,
  placeholderBuscar = 'Buscar…',
  filtrosActivos = [],
  onLimpiarFiltros,
  facetas = [],
  valoresDeFacetas = {},
  onFacetas,
  resultados,
  barra,
  onAbrirFila,
  accionesDeFila = [],
  accionesEnLote = [],
  exportarComo,
  vacio,
  porPagina = 50,
  memoria,
  agrupacion,
  columnasVisibles,
  onColumnasVisibles,
  densidad,
  onDensidad,
  sinBarraPropia = false,
}: DataTableProps<T>) {
  const [orden, setOrden] = useState<{ id: string; dir: Direccion } | null>(null);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [pagina, setPagina] = useState(0);
  // El menú «⋯» va fijo a la pantalla: dentro de la caja de la tabla lo recortaría su desplazamiento.
  const [menu, setMenu] = useState<{ id: string; boton: HTMLElement; cuantas: number; estilo: CSSProperties } | null>(null);

  const [compactaLocal, setCompactaLocal] = useState(() => leerMemoria(memoria, 'compacta') === '1');
  const [ocultasLocal, setOcultasLocal] = useState<Set<string>>(() => {
    const guardado = leerMemoria(memoria, 'ocultas');
    if (guardado != null) return new Set(guardado.split(',').filter(Boolean));
    return new Set(columnas.filter((c) => c.ocultaPorDefecto).map((c) => c.id));
  });
  const [plegados, setPlegados] = useState<Set<string>>(() => new Set((leerMemoria(memoria, 'plegados') ?? '').split(',').filter(Boolean)));
  const [selectorColumnas, setSelectorColumnas] = useState(false);

  const tablero = Boolean(agrupacion);
  const compacta = densidad ? densidad === 'compacta' : compactaLocal;
  const ocultas = useMemo(
    () => (columnasVisibles ? new Set(columnas.filter((c) => !c.fija && !columnasVisibles.includes(c.id)).map((c) => c.id)) : ocultasLocal),
    [columnas, columnasVisibles, ocultasLocal],
  );

  // Lo que se manda desde fuera no se recuerda aquí: lo guarda quien lo manda.
  const densidadDeFuera = densidad !== undefined;
  const columnasDeFuera = columnasVisibles !== undefined;
  useEffect(() => {
    if (!densidadDeFuera) guardarMemoria(memoria, 'compacta', compactaLocal ? '1' : '0');
  }, [compactaLocal, memoria, densidadDeFuera]);
  useEffect(() => {
    if (!columnasDeFuera) guardarMemoria(memoria, 'ocultas', [...ocultasLocal].join(','));
  }, [ocultasLocal, memoria, columnasDeFuera]);
  useEffect(() => {
    if (tablero) guardarMemoria(memoria, 'plegados', [...plegados].join(','));
  }, [plegados, memoria, tablero]);

  const menuAbierto = menu !== null;
  useEffect(() => {
    if (!menuAbierto) return;
    // Fijo a la pantalla, se quedaría flotando lejos de su fila si algo se desplaza: sigue a su botón.
    // (Cerrarlo al primer desplazamiento no sirve: el que llega justo después del clic lo cerraba al abrir.)
    const seguir = () => setMenu((m) => m && { ...m, estilo: posicionDelMenu(m.boton, m.cuantas) });
    window.addEventListener('scroll', seguir, true);
    window.addEventListener('resize', seguir);
    return () => {
      window.removeEventListener('scroll', seguir, true);
      window.removeEventListener('resize', seguir);
    };
  }, [menuAbierto]);

  // Filtrar y buscar es del módulo; ordenar es de la tabla.
  const visibles = useMemo(() => columnas.filter((c) => !ocultas.has(c.id)), [columnas, ocultas]);

  const ordenadas = useMemo(() => {
    if (!orden) return filas;
    const col = columnas.find((c) => c.id === orden.id);
    if (!col?.valor) return filas;
    const copia = [...filas];
    copia.sort((a, b) => {
      const va = col.valor!(a);
      const vb = col.valor!(b);
      // Lo vacío siempre al final, ordenes como ordenes: un hueco arriba de
      // todo hace parecer que la tabla está rota.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' });
      return orden.dir === 'asc' ? cmp : -cmp;
    });
    return copia;
  }, [filas, orden, columnas]);

  // ponytail: agrupada se dibuja todo, sin páginas ni virtualización; si un negocio pasa de
  // ~1.000 filas entra una librería con virtualización.
  const tanda = tablero ? 0 : porPagina;
  const totalPaginas = tanda > 0 ? Math.max(1, Math.ceil(ordenadas.length / tanda)) : 1;
  const paginaSegura = Math.min(pagina, totalPaginas - 1);
  const enPantalla = tanda > 0 ? ordenadas.slice(paginaSegura * tanda, (paginaSegura + 1) * tanda) : ordenadas;
  // Se reparten ya ordenadas: el orden por columna queda dentro de cada grupo.
  const grupos = useMemo(() => (agrupacion ? repartirEnGrupos(ordenadas, agrupacion) : null), [ordenadas, agrupacion]);

  // Si el filtro reduce las filas, volver a la primera página: quedarse en la
  // página 4 de un resultado de 3 filas muestra una tabla vacía que parece un
  // error.
  useEffect(() => setPagina(0), [filas.length, buscar]);

  const seleccionadas = useMemo(
    () => ordenadas.filter((f) => seleccion.has(idDe(f))),
    [ordenadas, seleccion, idDe],
  );
  const todasEnPantalla = enPantalla.length > 0 && enPantalla.every((f) => seleccion.has(idDe(f)));

  const hayTotales = visibles.some((c) => c.total);
  const hayLote = accionesEnLote.length > 0;
  const hayMenu = accionesDeFila.length > 0;
  // Agrupada, la casilla va dentro de la primera columna: no suma una celda.
  const celdasPorFila = visibles.length + (hayLote && !tablero ? 1 : 0) + (hayMenu ? 1 : 0);

  function alternarTodas() {
    setSeleccion((prev) => {
      const n = new Set(prev);
      if (todasEnPantalla) enPantalla.forEach((f) => n.delete(idDe(f)));
      else enPantalla.forEach((f) => n.add(idDe(f)));
      return n;
    });
  }

  function alternarColumna(id: string) {
    if (columnasVisibles) {
      // Se conservan los ids que hoy no son columna (un campo que todavía no cargó): quitarlos lo escondería al llegar.
      onColumnasVisibles?.(columnasVisibles.includes(id) ? columnasVisibles.filter((x) => x !== id) : [...columnasVisibles, id]);
      return;
    }
    setOcultasLocal((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function alternarDensidad() {
    if (densidad) onDensidad?.(compacta ? 'comoda' : 'compacta');
    else setCompactaLocal((v) => !v);
  }

  function alternarGrupo(clave: string) {
    setPlegados((prev) => {
      const n = new Set(prev);
      if (n.has(clave)) n.delete(clave);
      else n.add(clave);
      return n;
    });
  }

  function abrirMenu(e: MouseEvent<HTMLButtonElement>, id: string, cuantas: number) {
    const boton = e.currentTarget;
    setMenu({ id, boton, cuantas, estilo: posicionDelMenu(boton, cuantas) });
  }

  function exportar() {
    descargarCsv(exportarComo ?? 'tabla', csvDe(ordenadas, visibles));
  }

  // En el teléfono lo que se toca mide 44 px; solo agrupada, para no moverle nada a las otras pantallas.
  const toque = tablero && 'grid min-h-[44px] min-w-[44px] place-items-center md:min-h-0 md:min-w-0';
  // Agrupada, la casilla va envuelta: en el teléfono su zona de toque mide 44 px sin agrandar el cuadrito.
  const conToque = (casilla: ReactNode) => (tablero ? <label className={TOQUE_DE_CASILLA}>{casilla}</label> : casilla);
  const casillaDe = (id: string, marcada: boolean) =>
    conToque(
      <input
        type="checkbox"
        checked={marcada}
        onChange={() =>
          setSeleccion((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
          })
        }
        aria-label="Seleccionar"
        className={tablero ? CASILLA : undefined}
      />,
    );
  const casillaDeTodas = conToque(
    <input type="checkbox" checked={todasEnPantalla} onChange={alternarTodas} aria-label="Seleccionar todas" className={tablero ? CASILLA : undefined} />,
  );

  const dibujarFila = (fila: T, color?: string) => {
    const id = idDe(fila);
    const marcada = seleccion.has(id);
    const acciones = accionesDeFila.filter((a) => !a.ocultaSi?.(fila));
    return (
      <tr
        key={id}
        data-fila={id}
        onDoubleClick={(e) => {
          if (!(e.target as HTMLElement).closest(INTERACTIVO)) onAbrirFila?.(fila);
        }}
        className={cn(
          'bg-surface transition-colors hover:bg-slate-50',
          // Agrupada, el fondo es opaco: la primera columna lo hereda y tapa lo que pasa por debajo.
          tablero ? 'dark:hover:bg-slate-800' : 'dark:hover:bg-slate-900/40',
          marcada && (tablero ? 'bg-brand-50 hover:bg-brand-50 dark:bg-[#1b183f] dark:hover:bg-[#1b183f]' : 'bg-brand-50/60 dark:bg-brand-900/20'),
          // `select-none` siempre que la fila se pueda abrir: sin
          // esto el doble clic marca el texto de la celda en vez
          // de abrir el panel, y parece que no hizo nada.
          onAbrirFila && 'cursor-default select-none',
        )}
      >
        {hayLote && !tablero && <td className="px-3 py-2">{casillaDe(id, marcada)}</td>}
        {visibles.map((c, i) => {
          const primera = tablero && i === 0;
          return (
            <td
              key={c.id}
              className={cn(
                'px-3 align-middle',
                compacta ? 'py-1.5' : 'py-2.5',
                c.numerica && 'text-right tabular-nums',
                tablero && 'border-b border-surface-border',
                primera && 'sticky left-0 z-[1] border-r bg-inherit pl-4',
              )}
            >
              {primera ? (
                <div className="flex items-center md:gap-2.5">
                  {/* `-bottom-px`: tapa también la raya de abajo, para que la franja baje sin cortes. */}
                  <span aria-hidden className={cn('absolute -bottom-px left-0 top-0 w-1', color ?? SIN_COLOR)} />
                  {hayLote && casillaDe(id, marcada)}
                  {c.celda(fila)}
                </div>
              ) : (
                c.celda(fila)
              )}
            </td>
          );
        })}
        {hayMenu && (
          <td className={cn('px-3 py-2 text-right', tablero && 'border-b border-surface-border')}>
            {acciones.length > 0 && (
              <>
                <button
                  type="button"
                  title="Más acciones"
                  aria-haspopup="menu"
                  aria-expanded={menu?.id === id}
                  onClick={(e) => abrirMenu(e, id, acciones.length)}
                  className={cn('rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800', toque, tablero && 'ml-auto text-slate-500')}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
                {menu?.id === id && (
                  <>
                    <button type="button" aria-label="Cerrar" className="fixed inset-0 z-40 cursor-default" onClick={() => setMenu(null)} />
                    <div role="menu" style={menu.estilo} className="fixed z-50 w-48 rounded-lg border border-surface-border bg-surface py-1 text-left shadow-lg">
                      {acciones.map((a) => (
                        <button
                          key={a.etiqueta}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setMenu(null);
                            a.onClick(fila);
                          }}
                          className={cn(
                            'flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800',
                            tablero && 'min-h-[44px] md:min-h-0',
                            a.peligrosa
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-slate-700 dark:text-slate-200',
                          )}
                        >
                          {a.icono}
                          {a.etiqueta}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="space-y-3">
      {/* ── Barra ────────────────────────────────────────────────────────── */}
      {/* ── Superfiltro: buscar, facetas, contador y chips (Josué, 14-oct) ── */}
      {!sinBarraPropia && (
        <Superfiltro
          buscar={buscar}
          onBuscar={onBuscar}
          placeholder={placeholderBuscar}
          facetas={facetas}
          valores={valoresDeFacetas}
          onCambiar={onFacetas ?? (() => undefined)}
          onLimpiar={onLimpiarFiltros}
          chipsExtra={filtrosActivos}
          resultados={cargando || error ? undefined : resultados}
          acciones={
            <>
              {barra}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  title={compacta ? 'Ver más cómodo' : 'Ver más compacto'}
                  aria-pressed={compacta}
                  onClick={alternarDensidad}
                  className={cn('rounded-md p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800', toque)}
                >
                  <Rows3 className="h-4 w-4" />
                </button>

                <div className="relative">
                  <button
                    type="button"
                    title="Qué columnas ver"
                    aria-expanded={selectorColumnas}
                    onClick={() => setSelectorColumnas((v) => !v)}
                    className={cn('rounded-md p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800', toque)}
                  >
                    <Columns3 className="h-4 w-4" />
                  </button>
                  {selectorColumnas && (
                    <>
                      <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 z-10"
                        onClick={() => setSelectorColumnas(false)}
                      />
                      <div className={cn('absolute right-0 z-20 mt-1 w-56 rounded-lg border border-surface-border bg-surface p-2 shadow-lg', tablero && 'max-h-[60vh] overflow-y-auto')}>
                        <p className="px-2 pb-1 text-xs font-medium text-slate-500">Columnas</p>
                        {columnas.map((c) => (
                          <label
                            key={c.id}
                            className={cn(
                              'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800',
                              tablero && 'min-h-[44px] md:min-h-0',
                              c.fija && 'cursor-not-allowed opacity-50',
                            )}
                          >
                            <input type="checkbox" disabled={c.fija} checked={!ocultas.has(c.id)} onChange={() => alternarColumna(c.id)} className={tablero ? CASILLA : undefined} />
                            {c.titulo}
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {exportarComo && ordenadas.length > 0 && (
                  <button
                    type="button"
                    title="Bajar a Excel lo que estás viendo"
                    onClick={exportar}
                    className={cn('rounded-md p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800', toque)}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                )}
              </div>
            </>
          }
        />
      )}

      {/* Sin su barra, la pantalla trae la suya: aquí solo quedan los chips. */}
      {sinBarraPropia && filtrosActivos.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {filtrosActivos.map((f) => (
            <span
              key={f.clave}
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-1 pl-2.5 pr-1 text-xs text-brand-800 dark:bg-brand-900/30 dark:text-brand-200"
            >
              {f.etiqueta}
              <button
                type="button"
                onClick={f.onQuitar}
                className="rounded-full p-0.5 hover:bg-brand-100 dark:hover:bg-brand-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {onLimpiarFiltros && (
            <button
              type="button"
              onClick={onLimpiarFiltros}
              className="text-xs text-slate-500 underline hover:text-slate-700"
            >
              limpiar todo
            </button>
          )}
        </div>
      )}

      {/* ── Acciones en lote ─────────────────────────────────────────────── */}
      {seleccionadas.length > 0 && hayLote && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 dark:border-brand-800 dark:bg-brand-900/20">
          <span className="text-sm font-medium text-brand-900 dark:text-brand-100">
            {seleccionadas.length} seleccionada{seleccionadas.length === 1 ? '' : 's'}
          </span>
          {accionesEnLote.map((a) =>
            a.control ? (
              <Fragment key={a.etiqueta}>{a.control(seleccionadas)}</Fragment>
            ) : (
              <Button key={a.etiqueta} size="sm" variant="outline" className={cn(tablero && 'min-h-[44px] md:min-h-0')} onClick={() => a.onClick?.(seleccionadas)}>
                {a.icono}
                {a.etiqueta}
              </Button>
            ),
          )}
          <button
            type="button"
            onClick={() => setSeleccion(new Set())}
            className={cn('ml-auto text-xs text-brand-700 underline dark:text-brand-300', tablero && 'min-h-[44px] md:min-h-0')}
          >
            quitar selección
          </button>
        </div>
      )}

      {/* ── Los cuatro estados ───────────────────────────────────────────── */}
      {cargando ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <EstadoVacio
          icono={AlertTriangle}
          titulo="No se pudo cargar"
          descripcion={error}
          accion={onReintentar && <Button onClick={onReintentar}>Reintentar</Button>}
        />
      ) : ordenadas.length === 0 ? (
        <EstadoVacio
          icono={Inbox}
          titulo={vacio?.titulo ?? 'No hay nada acá'}
          descripcion={vacio?.descripcion}
          accion={vacio?.accion}
        />
      ) : (
        <>
          <div
            className={cn(
              'rounded-lg border border-surface-border',
              // Su propia caja de desplazamiento: solo así los encabezados y la primera columna se quedan fijos.
              tablero ? 'max-h-[calc(100dvh-11rem-var(--alto-de-pestanas,0px))] overflow-auto bg-surface-muted' : 'overflow-x-auto',
            )}
          >
            {/* Agrupada, las rayas son de cada celda (`border-separate`): con las de la tabla, una celda fija pierde la suya al quedarse quieta. */}
            <table className={cn('w-full min-w-[40rem] text-sm', tablero && 'border-separate border-spacing-0')}>
              <thead className={cn('text-left', !tablero && 'bg-slate-50 dark:bg-slate-900/40')}>
                <tr>
                  {hayLote && !tablero && <th className="w-10 px-3 py-2">{casillaDeTodas}</th>}
                  {visibles.map((c, i) => {
                    const activa = orden?.id === c.id;
                    const primera = tablero && i === 0;
                    const titulo = c.valor ? (
                      <button
                        type="button"
                        onClick={() =>
                          setOrden((prev) =>
                            prev?.id === c.id
                              ? { id: c.id, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                              : { id: c.id, dir: 'asc' },
                          )
                        }
                        className={cn(
                          'inline-flex items-center gap-1 hover:text-slate-800 dark:hover:text-slate-200',
                          c.numerica && 'flex-row-reverse',
                          activa && 'text-slate-900 dark:text-slate-100',
                        )}
                      >
                        {c.titulo}
                        {activa ? (
                          orden!.dir === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      c.titulo
                    );
                    return (
                      <th
                        key={c.id}
                        aria-sort={tablero && activa ? (orden!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                        className={cn(
                          'px-3 py-2 text-xs font-medium text-slate-500',
                          c.numerica && 'text-right',
                          c.ancho,
                          tablero && ENCABEZADO_FIJO,
                          primera && 'left-0 z-[3] border-r pl-4',
                        )}
                      >
                        {primera && hayLote ? (
                          <div className="flex items-center md:gap-2.5">
                            {casillaDeTodas}
                            {titulo}
                          </div>
                        ) : (
                          titulo
                        )}
                      </th>
                    );
                  })}
                  {hayMenu && <th className={cn('w-10 px-3 py-2', tablero && ENCABEZADO_FIJO)} />}
                </tr>
              </thead>

              {grupos ? (
                grupos.map((g) => {
                  const ids = g.filas.map(idDe);
                  const marcadas = ids.filter((id) => seleccion.has(id)).length;
                  const plegado = plegados.has(g.clave);
                  const pie = agrupacion?.pie && g.filas.length > 0 ? agrupacion.pie(g.filas) : null;
                  return (
                    <tbody key={g.clave} data-grupo={g.clave}>
                      <tr>
                        <th colSpan={celdasPorFila} scope="rowgroup" className="border-b border-surface-border bg-surface-muted p-0 text-left font-normal">
                          {/* Fijo a la izquierda: el nombre del grupo no se va al correr la tabla de lado. */}
                          <div className="sticky left-0 flex w-fit max-w-[calc(100vw-3rem)] items-center pb-1.5 pl-4 pr-4 pt-4 md:gap-2">
                            {hayLote &&
                              conToque(
                                <input
                                  type="checkbox"
                                  aria-label={`Seleccionar ${g.etiqueta}`}
                                  checked={ids.length > 0 && marcadas === ids.length}
                                  disabled={ids.length === 0}
                                  ref={(el) => {
                                    // Con solo algunas marcadas, la casilla del grupo queda a medias.
                                    if (el) el.indeterminate = marcadas > 0 && marcadas < ids.length;
                                  }}
                                  onChange={() => setSeleccion((prev) => marcarGrupo(prev, ids))}
                                  className={CASILLA}
                                />,
                              )}
                            <button
                              type="button"
                              aria-expanded={!plegado}
                              onClick={() => alternarGrupo(g.clave)}
                              className={
                                'flex min-h-[44px] min-w-0 items-center gap-2 rounded-md px-1.5 text-left transition-colors hover:bg-slate-200/60 ' +
                                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600 md:min-h-[32px] dark:hover:bg-slate-800'
                              }
                            >
                              <ChevronDown aria-hidden className={cn('h-4 w-4 shrink-0 text-slate-500 motion-safe:transition-transform', plegado && '-rotate-90')} />
                              <span aria-hidden className={cn('h-4 w-1.5 shrink-0 rounded-full', g.color ?? SIN_COLOR)} />
                              <span className="truncate text-[15px] font-semibold text-slate-900 dark:text-slate-100">{g.etiqueta}</span>
                              <span className="rounded-full bg-slate-200/80 px-2 py-0.5 text-xs font-medium tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                {g.filas.length}
                              </span>
                            </button>
                            {/* Plegado, el resumen sube al encabezado: sigue diciendo cuánto hay ahí dentro. */}
                            {plegado && pie && <span className="ml-2 truncate text-xs tabular-nums text-slate-500 md:ml-0 dark:text-slate-400">{pie}</span>}
                          </div>
                        </th>
                      </tr>
                      {!plegado && g.filas.map((f) => dibujarFila(f, g.color))}
                      {!plegado && pie && (
                        <tr>
                          <td colSpan={celdasPorFila} className="border-b border-surface-border bg-surface p-0">
                            <div className="sticky left-0 w-fit py-2 pl-4 pr-4 text-[13px] font-medium tabular-nums text-slate-600 dark:text-slate-300">{pie}</div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  );
                })
              ) : (
                <tbody className="divide-y divide-surface-border">{enPantalla.map((fila) => dibujarFila(fila))}</tbody>
              )}

              {hayTotales && (
                <tfoot className="border-t-2 border-surface-border bg-slate-50 font-medium dark:bg-slate-900/40">
                  <tr>
                    {hayLote && !tablero && <td />}
                    {visibles.map((c) => (
                      <td
                        key={c.id}
                        className={cn('px-3 py-2', c.numerica && 'text-right tabular-nums')}
                      >
                        {c.total?.(ordenadas)}
                      </td>
                    ))}
                    {hayMenu && <td />}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>
              {ordenadas.length} fila{ordenadas.length === 1 ? '' : 's'}
              {onAbrirFila && ' · doble clic para ver el detalle'}
            </span>
            {totalPaginas > 1 && (
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={paginaSegura === 0}
                  onClick={() => setPagina((p) => p - 1)}
                  className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-slate-800"
                >
                  Anterior
                </button>
                <span>
                  {paginaSegura + 1} de {totalPaginas}
                </span>
                <button
                  type="button"
                  disabled={paginaSegura >= totalPaginas - 1}
                  onClick={() => setPagina((p) => p + 1)}
                  className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-slate-800"
                >
                  Siguiente
                </button>
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Dónde va el menú «⋯» de una fila: pegado bajo su botón o, cerca del borde de abajo, encima (36 px por acción, a ojo). */
function posicionDelMenu(boton: Element, cuantas: number): CSSProperties {
  const r = boton.getBoundingClientRect();
  const vertical = r.bottom + cuantas * 36 + 16 > window.innerHeight ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 };
  return { ...vertical, right: Math.max(8, window.innerWidth - r.right) };
}

// Agrupada: los encabezados de columna se quedan arriba al bajar (el fondo, opaco, tapa las filas que pasan).
const ENCABEZADO_FIJO = 'sticky top-0 z-[2] whitespace-nowrap border-b border-surface-border bg-surface-muted dark:text-slate-400';
const CASILLA = 'h-4 w-4 shrink-0 accent-brand-600';
// 14 + 16 + 14 px en el teléfono; el margen negativo se come el aire de la celda, no el de la fila.
const TOQUE_DE_CASILLA = '-my-3.5 -ml-3.5 grid shrink-0 cursor-pointer place-items-center p-3.5 md:m-0 md:p-0';
const SIN_COLOR = 'bg-slate-300 dark:bg-slate-600';

function EstadoVacio({
  icono: Icono,
  titulo,
  descripcion,
  accion,
}: {
  icono: typeof Inbox;
  titulo: string;
  descripcion?: string;
  accion?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-surface-border py-14 text-center">
      <Icono className="h-8 w-8 text-slate-300" />
      <p className="font-medium text-slate-900 dark:text-slate-100">{titulo}</p>
      {descripcion && <p className="max-w-sm text-sm text-slate-500">{descripcion}</p>}
      {accion && <div className="mt-2">{accion}</div>}
    </div>
  );
}

// Las preferencias de vista son del navegador de cada quien, no del negocio:
// no viajan al servidor. Si el almacenamiento falla (ventana privada), la tabla
// funciona igual con los valores de por defecto.
function leerMemoria(memoria: string | undefined, clave: string): string | null {
  if (!memoria) return null;
  try {
    return localStorage.getItem(`tabla:${memoria}:${clave}`);
  } catch {
    return null;
  }
}
function guardarMemoria(memoria: string | undefined, clave: string, valor: string): void {
  if (!memoria) return;
  try {
    localStorage.setItem(`tabla:${memoria}:${clave}`, valor);
  } catch {
    /* sin memoria, la tabla sigue andando */
  }
}
